import { FC, ReactNode } from 'react';
import {
  BoxProps,
  CloseButton,
  Divider,
  Grid,
  GridProps,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react';
import { FlexColumn } from 'components/layout/FlexColumn/FlexColumn';
import { FlexRow } from 'components/layout/FlexRow';
import 'components/widgets/InfoModal/style.scss';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  header: JSX.Element;
  children?: ReactNode;
  sx?: object;
};

type RowProps = GridProps & { hideDivider?: boolean; alignLabelTop?: boolean };

type Elements = {
  Row: FC<RowProps>;
  RowDetails: FC<BoxProps>;
  Footer: FC<BoxProps>;
};

const modalBodyStyle = {
  px: 0,
};

const modalPadding = {
  padding: 'padding_24',
};

const gridGap = {
  gap: 'padding_16',
};

const infoGrid = {
  pr: 'padding_8',
  alignItems: 'baseline',
  justifyContent: 'center',
};

const footerRow = {
  justifyContent: 'normal',
  gap: 'padding_16',
};

const headerStyle = {
  px: 0,
  paddingTop: 0,
  paddingBottom: 'padding_16',
};

const dividerStyle = {
  margin: '16px 0 16px',
};

export const InfoModal: FC<Props> & Elements = ({
  onClose,
  isOpen,
  header,
  children,
  sx,
}) => {
  return (
    <Modal size="3xl" isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent sx={{ ...sx, ...modalPadding }}>
        <ModalHeader sx={headerStyle}>
          <FlexRow className="modal-heading">
            <FlexRow.Left sx={gridGap}>{header}</FlexRow.Left>
            <CloseButton onClick={onClose} />
          </FlexRow>
        </ModalHeader>
        <ModalBody pt={0} sx={modalBodyStyle}>
          {children}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

InfoModal.Row = function Row({ children, hideDivider, alignLabelTop, ...props }) {
  return (
    <>
      {!hideDivider && <Divider sx={dividerStyle} />}
      <Grid
        {...props}
        templateColumns="1fr 3fr"
        sx={{
          ...infoGrid,
          ...(hideDivider ? { marginTop: '8px' } : {}),
          ...(alignLabelTop ? { alignItems: 'flex-start' } : {}),
        }}
      >
        {children}
      </Grid>
    </>
  );
};

InfoModal.RowDetails = function RowDetails({ children, ...props }) {
  return (
    <FlexRow
      textTransform="full-size-kana"
      fontSize="sm"
      justifyContent="left"
      alignItems="baseline"
      width="100%"
      minWidth={0}
      {...props}
    >
      {children}
    </FlexRow>
  );
};

InfoModal.Footer = function RowFooter({ children, ...props }) {
  return (
    <FlexColumn>
      <Divider sx={dividerStyle} />
      <FlexRow {...props} sx={footerRow}>
        {children}
      </FlexRow>
    </FlexColumn>
  );
};
